import { z } from 'zod';
// Closing a handover (BR-013…BR-015): the rules are decided by the server only, the same for every
// screen; this module only describes them. A completed handover and a refused one are both closed
// and never change afterwards (a correction process, BR-016, is not implemented).
export const CLOSING_OUTCOMES = ['completed', 'refused'];
export const CLOSING_OUTCOME_LABELS = {
    completed: 'הושלמה',
    refused: 'לא הושלמה – סירוב לחתימה',
};
export const SIGNER_KINDS = ['purchaser', 'representative', 'company'];
export const SIGNER_KIND_LABELS = {
    purchaser: 'רוכש/ת',
    representative: 'מיופה/ת כוח',
    company: 'נציג/ת החברה',
};
/** What still stands between the draft and its completion (the server's own list). */
export const CLOSING_PROBLEMS = [
    'acknowledgment_missing',
    'inspection_not_started',
    'items_unchecked',
    'general_checks_unchecked',
    'reinspections_pending',
    'findings_without_photo',
    'recipient_signature_missing',
    'company_signature_missing',
];
export const closingProblemSchema = z
    .object({ code: z.enum(CLOSING_PROBLEMS), count: z.number().int().min(0).nullable() })
    .strict();
export const signatureInfoSchema = z
    .object({
    id: z.uuid(),
    signerKind: z.enum(SIGNER_KINDS),
    signerName: z.string(),
    representativeReference: z.string().nullable(),
    signedAt: z.iso.datetime({ offset: true }),
})
    .strict();
export const acknowledgmentSchema = z
    .object({
    id: z.uuid(),
    version: z.number().int().min(1),
    text: z.string(),
    source: z.string(),
})
    .strict();
/** The closing part of a handover document. */
export const handoverClosingSchema = z
    .object({
    /** The organization's current wording (a draft), or the one the handover was closed under. */
    acknowledgment: acknowledgmentSchema.nullable(),
    recipientSignature: signatureInfoSchema.nullable(),
    companySignature: signatureInfoSchema.nullable(),
    /** A draft: what still prevents completion (empty: it can be completed). */
    problems: z.array(closingProblemSchema),
    closed: z
        .object({
        outcome: z.enum(CLOSING_OUTCOMES),
        at: z.iso.datetime({ offset: true }),
        by: z.string(),
        refusalReason: z.string().nullable(),
    })
        .strict()
        .nullable(),
})
    .strict();
export const SIGNATURE_LIMITS = { bytes: 600_000, name: 120, reference: 200 };
/**
 * `POST /projects/:id/handovers/:handoverId/signatures`: the signature for one role (replacing an
 * earlier one while the handover is a draft), for the version the signer saw (`rowVersion`). Any
 * later change to the draft removes its signatures. The image is a PNG drawn on the device,
 * base64. The company's signature is always the signed-in employee's.
 */
export const signatureRequestSchema = z
    .object({
    rowVersion: z.number().int().min(1),
    role: z.enum(['recipient', 'company']),
    signerKind: z.enum(SIGNER_KINDS),
    signerName: z.string().trim().min(2).max(SIGNATURE_LIMITS.name),
    representativeReference: z.string().trim().min(2).max(SIGNATURE_LIMITS.reference).optional(),
    image: z
        .string()
        .regex(/^data:image\/png;base64,[A-Za-z0-9+/]+=*$/)
        .max(Math.ceil((SIGNATURE_LIMITS.bytes * 4) / 3) + 32),
})
    .strict()
    .superRefine((v, context) => {
    if ((v.role === 'company') !== (v.signerKind === 'company'))
        context.addIssue({ code: 'custom', path: ['signerKind'], message: 'invalid' });
    if ((v.signerKind === 'representative') !== (v.representativeReference !== undefined))
        context.addIssue({ code: 'custom', path: ['representativeReference'], message: 'required' });
});
/** `POST /projects/:id/handovers/:handoverId/close`: completes the handover, or records a refusal. */
export const closeRequestSchema = z
    .object({
    rowVersion: z.number().int().min(1),
    outcome: z.enum(CLOSING_OUTCOMES),
    refusalReason: z.string().trim().min(3).max(500).optional(),
})
    .strict()
    .superRefine((v, context) => {
    if ((v.outcome === 'refused') !== (v.refusalReason !== undefined))
        context.addIssue({ code: 'custom', path: ['refusalReason'], message: 'required' });
});
/** `GET` / `POST /handover-acknowledgment`: the organization's wording (a business Super Admin). */
export const acknowledgmentRequestSchema = z
    .object({
    text: z.string().trim().min(20).max(4000),
    source: z.string().trim().min(3).max(300),
})
    .strict();
export const acknowledgmentResponseSchema = z
    .object({ current: acknowledgmentSchema.nullable(), canEdit: z.boolean() })
    .strict();
//# sourceMappingURL=handover-closing.js.map